import React, { useCallback, useEffect, useMemo, useState } from 'react'
import DocumentTitle from 'react-document-title'
import { useParams } from 'react-router'
import classNames from 'classnames'
import compact from 'lodash/compact'
import Button from 'transfix-ui/components/Button'
import Spinner from 'transfix-ui/components/Spinner'
import { Box, Divider, makeStyles } from 'transfix-ui/core'
import Typography from 'transfix-ui/core/Typography'
import { IIssueAutomationRuleActionEnum, IShipmentStates } from 'types/graphqlTypes'
import { StringParam } from 'use-query-params'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import RouteNotFound from 'components/404'
import { generatePageTitle } from 'utils/generateAppDefaults'
import { useModal, useUserDetails, useQueryParams } from 'utils/hooks'
import { useIssuesContext } from '../IssuesContext'
import IssuesWrap from '../IssuesWrap'
import { DeleteRuleModal } from './components/DeleteRuleModal'
import IssueRulesTable from './components/IssueRulesTable'
import { TIssueRuleFormFormSchema } from './components/RuleForm'
import RuleFormSection from './components/RuleFormSection'
import { SaveRuleAsModal } from './components/SaveRuleAsModal'
import { SelectRuleTypeModal } from './components/SelectRuleTypeModal'
import { useCreateIssueAutomationRuleMutation } from './graphql/CreateIssueAutomationRule'
import { useDeleteIssueAutomationRuleMutation } from './graphql/DeleteIssueAutomationRule'
import { IIssueAutomationRuleFragment } from './graphql/IssueAutomationRuleFragment'
import { useIssueAutomationRulesByIssueTypeQuery } from './graphql/IssueAutomationRulesByIssueType'
import { useUpdateIssueAutomationRuleMutation } from './graphql/UpdateIssueAutomationRule'
import { getIssueAutomationRuleNodes, getIssueType } from './utils'

const NEW_RULE_MODAL = 'new-rule-modal'
const NEW_RULE_FORM = 'new-rule-form'
const SAVE_RULE_AS_MODAL = 'save-rule-as-modal'
const CREATE_RULE_MODAL = 'create-rule-modal'
const DELETE_RULE_MODAL = 'delete-rule-modal'
const EDIT_RULE_FORM = 'edit-rule-form'

const useStyles = makeStyles(theme => ({
  containerForm: {
    display: 'flex',
    paddingRight: 0,
    paddingLeft: 0,
    paddingTop: 0,
    '& > div:first-child': {
      width: '75%',
      marginTop: theme.spacing(2),
      marginLeft: theme.spacing(3.5),
      marginRight: theme.spacing(3.5),
    },
    '& > div:last-child': {
      width: '35%',
    },
  },
}))

export interface IAutomationPageQueryParams {
  modal?: string
  form?: string
  ruleType?: string
  ruleId?: string
}

export interface IIssueAutomationError {
  message: string
  title?: string
  key?: string
}

/**
 * Issue Automation Page
 * @route /issues/automation
 */
const AutomationPage = () => {
  const classes = useStyles()
  const [currentFormData, setCurrentFormData] = useState<TIssueRuleFormFormSchema | null>(null)
  const [currentFormError, setCurrentFormError] = useState<IIssueAutomationError | undefined>(
    undefined
  )
  const [currentFormWarning, setCurrentFormWarning] = useState<IIssueAutomationError | undefined>(
    undefined
  )
  const { data, loadingIssueTypes } = useIssuesContext()
  const isMpShipmentIssueAutomationEnabled = useFeatureFlag('mpShipmentIssueAutomation')
  const { userPermissions } = useUserDetails()
  const hasEditPermission = userPermissions?.canEditIssueAutomation
  const { id } = useParams<{ id: string }>()
  const [
    { form: activeForm, modal: activeModal, ruleId: selectedRuleId, ruleType: selectedRuleType },
    setQueryParams,
  ] = useQueryParams<IAutomationPageQueryParams>({
    modal: StringParam,
    form: StringParam,
    ruleType: StringParam,
    ruleId: StringParam,
  })
  const { closeModal, isOpen, openModal } = useModal()
  const [
    deleteIssueAutomationRule,
    { loading: loadingDeleteIssue },
  ] = useDeleteIssueAutomationRuleMutation({
    variables: {
      input: {
        id: selectedRuleId || '',
      },
    },
  })
  const [
    updateIssueAutomationRule,
    { loading: loadingUpdateIssueRule },
  ] = useUpdateIssueAutomationRuleMutation({
    variables: {
      input: {
        id: selectedRuleId || '',
      },
    },
  })

  const [
    createIssueAutomationRule,
    { loading: loadingCreateIssueRule },
  ] = useCreateIssueAutomationRuleMutation()

  const issueType = useMemo(() => {
    return getIssueType(id, data.types, data.categories)
  }, [data.categories, data.types, id])

  const { data: rulesData, loading: loadingIssueRules } = useIssueAutomationRulesByIssueTypeQuery({
    variables: {
      shipmentIssueTypeId: issueType?.id.toString(),
    },
    skip: !issueType,
  })

  const handleReplaceQueryParams = useCallback(
    (paramsToReplace: IAutomationPageQueryParams) => {
      setQueryParams(paramsToReplace, 'replaceIn')
    },
    [setQueryParams]
  )

  /**
   * Open modal when modal query parameter is set
   */
  useEffect(() => {
    if (activeModal === NEW_RULE_MODAL) {
      openModal(NEW_RULE_MODAL)()
    } else if (activeModal === SAVE_RULE_AS_MODAL && selectedRuleId) {
      openModal(SAVE_RULE_AS_MODAL)()
    } else if (activeModal === DELETE_RULE_MODAL && selectedRuleId) {
      openModal(DELETE_RULE_MODAL)()
    } else if (activeModal === CREATE_RULE_MODAL && !currentFormData) {
      handleReplaceQueryParams({ modal: undefined })
    } else if (activeModal === CREATE_RULE_MODAL && currentFormData) {
      openModal(CREATE_RULE_MODAL)()
    }
  }, [activeModal, currentFormData, openModal, selectedRuleId, handleReplaceQueryParams])

  const handleCloseModal = () => {
    handleReplaceQueryParams({ modal: undefined, ruleId: undefined })
    closeModal()
    setCurrentFormError(undefined)
  }

  const handleContinue = (newRuleType: string) => {
    handleReplaceQueryParams({ modal: undefined, ruleType: newRuleType, form: NEW_RULE_FORM })
    closeModal()
  }

  const handleCloseForm = () => {
    handleReplaceQueryParams({
      modal: undefined,
      ruleType: undefined,
      form: undefined,
      ruleId: undefined,
    })
    setCurrentFormData(null)
    setCurrentFormError(undefined)
  }

  const handleCreateIssueRule = async (newName: string) => {
    if (!currentFormData) {
      return
    }

    const newNodes = getIssueAutomationRuleNodes(currentFormData)

    const createResult = await createIssueAutomationRule({
      variables: {
        input: {
          name: newName,
          ruleAction: currentFormData.ruleType as IIssueAutomationRuleActionEnum,
          shipmentIssueTypeId: issueType.id.toString(),
          issueAutomationRuleNodes: newNodes,
        },
      },
      refetchQueries: ['IssueAutomationRulesByIssueType'],
    })
    const createError =
      createResult?.errors?.toString() ||
      createResult?.data?.createIssueAutomationRule?.errors?.map(e => e.message).join('. ')
    if (!createError) {
      handleCloseModal()
      handleCloseForm()
    } else {
      setCurrentFormError({
        message: createError,
      })
    }
  }

  const handleDeleteIssueRule = async () => {
    if (!selectedRuleId) {
      return
    }
    const deleteResult = await deleteIssueAutomationRule({
      variables: {
        input: {
          id: selectedRuleId,
        },
      },
      refetchQueries: ['IssueAutomationRulesByIssueType'],
    })
    const deleteError =
      deleteResult?.errors?.toString() ||
      deleteResult?.data?.deleteIssueAutomationRule?.errors?.map(e => e.message).join('. ')
    if (!deleteError) {
      handleCloseModal()
    } else {
      setCurrentFormError({
        message: deleteError,
      })
    }
  }

  const handleUpdateIssueRuleName = async (newName: string) => {
    if (!selectedRuleId) {
      return
    }
    const updateResult = await updateIssueAutomationRule({
      variables: {
        input: {
          id: selectedRuleId,
          name: newName,
        },
      },
      refetchQueries: ['IssueAutomationRulesByIssueType'],
    })
    const updateError =
      updateResult?.errors?.toString() ||
      updateResult?.data?.updateIssueAutomationRule?.errors?.map(e => e.message).join('. ')
    if (!updateError) {
      handleCloseModal()
    } else {
      setCurrentFormError({
        message: updateError,
      })
    }
  }

  const handleUpdateIssueRule = async (formData: TIssueRuleFormFormSchema) => {
    if (!selectedRuleId) {
      return
    }

    const newNodes = getIssueAutomationRuleNodes(formData)

    const updateResult = await updateIssueAutomationRule({
      variables: {
        input: {
          id: selectedRuleId,
          issueAutomationRuleNodes: newNodes,
        },
      },
      refetchQueries: ['IssueAutomationRulesByIssueType'],
    })
    const updateError =
      updateResult?.errors?.toString() ||
      updateResult?.data?.updateIssueAutomationRule?.errors?.map(e => e.message).join('. ')
    if (!updateError) {
      handleCloseModal()
      handleCloseForm()
    } else {
      setCurrentFormError({
        message: updateError,
      })
    }
  }

  const handleSaveForm = (formData: TIssueRuleFormFormSchema) => {
    setCurrentFormError(undefined)
    setCurrentFormWarning(undefined)

    const hasShipmentInformation = formData.nodes.some(node => node.isShipmentInformation)
    const hasShipmentEvent = formData.nodes.some(node => node.isShipmentEvent)

    if (hasShipmentInformation) {
      const shipmentStatusNodes = formData.nodes.filter(node => node.isShipmentStatus)
      const hasShipmentStatus = shipmentStatusNodes.length > 0

      if (!hasShipmentStatus) {
        setCurrentFormError({
          title: 'Shipment status required',
          message: 'Specify a shipment status when using Shipment Info as rule.',
          key: 'SHIPMENT_STATUS_REQUIRED',
        })
        return
      }

      const hasCanceledOrCompletedStatuses =
        shipmentStatusNodes[0].shipmentStatusFields.includes(IShipmentStates.canceled) ||
        shipmentStatusNodes[0].shipmentStatusFields.includes(IShipmentStates.completed)

      if (hasShipmentStatus && hasCanceledOrCompletedStatuses) {
        setCurrentFormWarning({
          title: 'Some shipment statuses are not supported with Shipment Info',
          message:
            'The following statuses are not supported and have been disabled: Completed, and Cancelled.',
          key: 'SHIPMENT_STATUS_WARNING',
        })
        return
      }
    }
    if (hasShipmentEvent) {
      const shipmentStatusNodes = formData.nodes.filter(node => node.isShipmentStatus)
      const hasShipmentStatus = shipmentStatusNodes.length > 0

      if (!hasShipmentStatus) {
        setCurrentFormError({
          title: 'Shipment status required',
          message: 'Specify a shipment status when using Shipment Event as rule.',
          key: 'SHIPMENT_STATUS_REQUIRED',
        })
        return
      }

      const hasInvalidStatuses = shipmentStatusNodes[0].shipmentStatusFields.some(
        field => field !== IShipmentStates.on_hold && field !== IShipmentStates.tendered
      )
      if (hasShipmentStatus && hasInvalidStatuses) {
        setCurrentFormError({
          title: 'Unable to create or update rule',
          message:
            'Same Day Shipment Events can only be applied to the following Shipment Statuses: Tendered, On Hold.',
          key: 'INVALID_SHIPMENT_STATUS',
        })
        return
      }
    }

    if (selectedRuleId) {
      handleUpdateIssueRule(formData)
    } else {
      setCurrentFormData(formData)
      handleReplaceQueryParams({ modal: CREATE_RULE_MODAL })
    }
  }

  if (loadingIssueTypes || !rulesData || loadingIssueRules) {
    return <Spinner />
  }

  if (!isMpShipmentIssueAutomationEnabled || (!loadingIssueTypes && !issueType)) {
    return <RouteNotFound />
  }

  const isNewRuleFormOpen = activeForm === NEW_RULE_FORM
  const isEditRuleFormOpen = activeForm === EDIT_RULE_FORM
  const rules: IIssueAutomationRuleFragment[] =
    compact(rulesData?.issueAutomationRulesByIssueType.nodes) || []
  const selectedRule = rules.find(rule => rule.id === selectedRuleId) || null

  return (
    <>
      <Box px={3} mb={2} display='flex' alignItems='center' justifyContent='space-between'>
        <Typography variant='h2'>{issueType.name} - Automated Rules</Typography>
        <Button
          color='primary'
          onClick={() => handleReplaceQueryParams({ modal: NEW_RULE_MODAL })}
          disabled={!hasEditPermission || isNewRuleFormOpen || isEditRuleFormOpen}>
          Add New Rule
        </Button>
      </Box>
      <Divider />
      <Box
        px={6}
        pt={2}
        className={classNames({
          [classes.containerForm]: isEditRuleFormOpen || isNewRuleFormOpen,
        })}>
        <div>
          <IssueRulesTable
            canEdit={hasEditPermission || false}
            onRename={(ruleId: string) =>
              handleReplaceQueryParams({ modal: SAVE_RULE_AS_MODAL, ruleId })
            }
            rules={rules}
            onDelete={(ruleId: string) =>
              handleReplaceQueryParams({ modal: DELETE_RULE_MODAL, ruleId })
            }
            onUpdate={(ruleId: string) => {
              handleReplaceQueryParams({
                modal: undefined,
                ruleId,
                form: EDIT_RULE_FORM,
                ruleType: undefined,
              })
            }}
            onNewRule={() => handleReplaceQueryParams({ modal: NEW_RULE_MODAL })}
            selectedRuleId={selectedRuleId || null}
          />
        </div>
        {(isNewRuleFormOpen || isEditRuleFormOpen) && (
          <RuleFormSection
            ruleType={
              (selectedRuleType as IIssueAutomationRuleActionEnum) ||
              IIssueAutomationRuleActionEnum.create
            }
            onCancel={handleCloseForm}
            onSubmit={handleSaveForm}
            selectedRule={selectedRule}
            disabled={!hasEditPermission || loadingUpdateIssueRule}
            error={currentFormError}
            warning={currentFormWarning}
          />
        )}
      </Box>
      {isOpen(NEW_RULE_MODAL) && (
        <SelectRuleTypeModal onClose={handleCloseModal} onContinue={handleContinue} />
      )}
      {isOpen(SAVE_RULE_AS_MODAL) && (
        <SaveRuleAsModal
          name={selectedRule?.name || ''}
          onClose={handleCloseModal}
          onSave={handleUpdateIssueRuleName}
          disabled={!hasEditPermission || loadingUpdateIssueRule}
          error={currentFormError?.message}
        />
      )}
      {isOpen(CREATE_RULE_MODAL) && (
        <SaveRuleAsModal
          name=''
          onClose={handleCloseModal}
          onSave={handleCreateIssueRule}
          disabled={!hasEditPermission || loadingCreateIssueRule}
          error={currentFormError?.message}
        />
      )}
      {isOpen(DELETE_RULE_MODAL) && (
        <DeleteRuleModal
          onClose={handleCloseModal}
          onConfirm={handleDeleteIssueRule}
          disabled={!hasEditPermission || loadingDeleteIssue}
          error={currentFormError?.message}
        />
      )}
    </>
  )
}

const AutomationPageWrap = () => (
  <DocumentTitle title={generatePageTitle('Issue Automation')}>
    <IssuesWrap>
      <AutomationPage />
    </IssuesWrap>
  </DocumentTitle>
)

export default AutomationPageWrap
